/**
 * Intervention slide panel state.
 *
 * ## API Migration Notes
 *
 * When the real API is available:
 * - `openPanel(patient, note?)` → `openPanel(patient, noteId?: string)`.
 *   In view mode, pass the note ID and fetch the full note via
 *   `GET /api/notes/{noteId}` inside the panel component.
 *   In new mode (noteId undefined), render the create form.
 * - The panel component should handle loading/error states from the fetch.
 * - `ConsultationNote` can be removed from the store — store only
 *   `open`, `patient`, and `noteId`.
 */
import { create } from 'zustand';
import type { PatientListItem } from '@/api/adapters/patients';
import type { ConsultationNote } from '@/features/patients/intervention-data';

/**
 * Values a 예정 일정 hands the new-intervention form when it is opened through [중재 시작].
 *
 * PRD 04 §3.4 ②-1 (SHCT-599) — the panel opens "해당 일정의 값으로 채워". Only the fields the
 * intervention form actually owns are carried:
 *   · 일시  → `callDateTime`
 *   · 유형  → only 전화(`call`) has a counterpart; the form's 유형 is 전화/메시지, while a schedule is
 *             외래·대면·전화·재방문, so 외래·대면·재방문 have nothing to map onto and are left at the
 *             form's own default rather than guessed at.
 *   · 환자  → already carried by `patient`
 *   · 담당 의료진 → not a form field (it comes from the auth context), so nothing to seed
 *
 * `scheduleId` is what the save has to link back to (`Schedule.interventionId`, the sole basis for
 * `done`). The link itself is a BE dependency — see the handover note.
 */
export interface InterventionPrefill {
	scheduleId: string;
	/** ISO-ish `YYYY-MM-DDTHH:mm` from the schedule row. */
	scheduledAt: string;
	/** Lowercase FE schedule code: visit | inperson | call | fu. */
	scheduleType: string;
}

interface InterventionPanelState {
	open: boolean;
	patient: PatientListItem | null;
	note: ConsultationNote | null; // null = new mode
	/** Set only when the panel was opened from a 예정 일정 via [중재 시작]. */
	prefill: InterventionPrefill | null;
	openPanel: (
		patient: PatientListItem,
		note?: ConsultationNote,
		prefill?: InterventionPrefill,
	) => void;
	closePanel: () => void;
}

export const useInterventionPanelStore = create<InterventionPanelState>(
	(set) => ({
		open: false,
		patient: null,
		note: null,
		prefill: null,
		openPanel: (patient, note, prefill) =>
			set({
				open: true,
				patient,
				note: note ?? null,
				prefill: prefill ?? null,
			}),
		closePanel: () =>
			set({ open: false, patient: null, note: null, prefill: null }),
	}),
);
